// Search autocomplete via NYC GeoSearch with keyboard navigation and recent searches
import { apiGet } from './api.js';
import { perfOpt } from './performance.js';
import { debounce } from './utils.js';

let currentResults = [];
let selectedIndex = -1;
const RECENT_KEY = 'pdd_recent_searches';
const MAX_RECENT = 5;

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function saveRecentSearch(result) {
  if (!result?.bbl) return;
  const recent = getRecentSearches().filter(r => r.bbl !== result.bbl);
  recent.unshift({ label: result.label, bbl: result.bbl, borough: result.borough, lat: result.lat, lng: result.lng });
  if (recent.length > MAX_RECENT) recent.length = MAX_RECENT;
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(recent)); } catch {}
}

export function initSearch(onSelect) {
  const input = document.getElementById('search-input');
  const dropdown = document.getElementById('search-results');
  if (!input || !dropdown) return;

  // Clear search input on every page show (back-navigation, bfcache, autofill)
  const clearSearch = () => { input.value = ''; dropdown.classList.remove('active'); selectedIndex = -1; };
  clearSearch();
  window.addEventListener('pageshow', clearSearch);
  setTimeout(clearSearch, 50);
  setTimeout(clearSearch, 300);

  const doSearch = debounce(async (text) => {
    if (text.length < 2) {
      // Show recent searches when input is focused but short
      showRecentSearches(dropdown, onSelect);
      return;
    }

    // Show loading state
    dropdown.innerHTML = '<div class="search-loading">Searching...</div>';
    dropdown.classList.add('active');

    try {
      const data = await apiGet(`/api/search?text=${encodeURIComponent(text)}`);
      currentResults = data.results || [];
      selectedIndex = -1;
      renderResults(dropdown, currentResults, onSelect);
    } catch (e) {
      dropdown.innerHTML = '<div class="search-loading">Search unavailable</div>';
      setTimeout(() => dropdown.classList.remove('active'), 2000);
    }
  }, 250);

  input.addEventListener('input', (e) => doSearch(e.target.value.trim()));

  // Show recent searches on focus when empty
  input.addEventListener('focus', () => {
    if (!input.value.trim()) {
      showRecentSearches(dropdown, onSelect);
    }
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (e) => {
    if (!input.contains(e.target) && !dropdown.contains(e.target)) {
      dropdown.classList.remove('active');
      selectedIndex = -1;
    }
  });

  // Keyboard navigation
  input.addEventListener('keydown', (e) => {
    const items = dropdown.querySelectorAll('.search-result-item');
    if (!items.length) {
      if (e.key === 'Escape') dropdown.classList.remove('active');
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
      updateSelection(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, -1);
      updateSelection(items);
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      items[selectedIndex]?.click();
    } else if (e.key === 'Escape') {
      dropdown.classList.remove('active');
      selectedIndex = -1;
      input.blur();
    }
  });
}

function showRecentSearches(dropdown, onSelect) {
  const recent = getRecentSearches();
  if (!recent.length) {
    dropdown.classList.remove('active');
    return;
  }

  dropdown.innerHTML = `
    <div class="search-section-label">Recent Searches</div>
    ${recent.map((r, i) => `
      <div class="search-result-item recent-item" data-index="${i}">
        <div class="label">${escapeHtml(r.label)}</div>
        <div class="sub">${formatResultSub(r)}</div>
      </div>
    `).join('')}
  `;

  dropdown.classList.add('active');
  selectedIndex = -1;

  dropdown.querySelectorAll('.search-result-item').forEach(el => {
    el.addEventListener('click', () => {
      const idx = parseInt(el.dataset.index);
      const result = recent[idx];
      document.getElementById('search-input').value = result.label;
      dropdown.classList.remove('active');
      if (onSelect) onSelect(result);
    });
  });
}

function updateSelection(items) {
  items.forEach((item, i) => {
    item.classList.toggle('selected', i === selectedIndex);
    if (i === selectedIndex) {
      item.scrollIntoView({ block: 'nearest' });
    }
  });
}

function renderResults(dropdown, results, onSelect) {
  if (!results.length) {
    dropdown.innerHTML = '<div class="search-loading">No results found</div>';
    setTimeout(() => dropdown.classList.remove('active'), 2000);
    return;
  }

  dropdown.innerHTML = results.map((r, i) => `
    <div class="search-result-item" data-index="${i}">
      <div class="label">${escapeHtml(r.label)}</div>
      <div class="sub">${formatResultSub(r)}</div>
    </div>
  `).join('');

  dropdown.classList.add('active');

  dropdown.querySelectorAll('.search-result-item').forEach(el => {
    el.addEventListener('click', () => {
      const idx = parseInt(el.dataset.index);
      const result = results[idx];
      document.getElementById('search-input').value = result.label;
      dropdown.classList.remove('active');
      saveRecentSearch(result);
      if (onSelect) onSelect(result);
    });
  });
}

function formatResultSub(r) {
  if (r.type === 'nj') {
    return `${escapeHtml(r.municipality || '')}${r.county ? `, ${escapeHtml(r.county)} County` : ''} \u00B7 New Jersey`;
  }
  if (r.type === 'nc') {
    return `${escapeHtml(r.city || '')}${r.county ? `, ${escapeHtml(r.county)} County` : ''} \u00B7 North Carolina`;
  }
  if (r.type === 'ca') {
    return `${escapeHtml(r.city || '')}${r.zipcode ? ` ${escapeHtml(r.zipcode)}` : ''} \u00B7 California`;
  }
  if (r.type === 'nys') {
    return `${escapeHtml(r.county || '')} County \u00B7 ${escapeHtml(r.propertyClass || 'NY State')}`;
  }
  if (r.type === 'phl') {
    return `Philadelphia \u00B7 Pennsylvania${r.opa ? ` \u00B7 OPA ${escapeHtml(r.opa)}` : ''}`;
  }
  if (r.type === 'il') {
    return `${escapeHtml(r.city || '')}${r.county ? `, ${escapeHtml(r.county)} County` : ''} \u00B7 Illinois`;
  }
  if (r.type === 'ct') {
    return `${escapeHtml(r.city || '')}${r.zipcode ? ` ${escapeHtml(r.zipcode)}` : ''} \u00B7 Connecticut`;
  }
  return `${r.bbl ? `BBL: ${r.bbl}` : ''}${r.borough ? ` \u00B7 ${escapeHtml(r.borough)}` : ''}`;
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
